import React from 'react';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { useMargins } from '../hooks/useMargins';
import { adjustMarginsForLabel, generateLabels } from '../compute/labels';
import { computeScales } from '../compute/scales';
import { formatAxis, formatBarChartData } from '../helpers';

const DEFAULT_SCALES = { x: {}, y: {} };

const ChartScaleContext = React.createContext({
  scales: DEFAULT_SCALES,
  margins: { left: 0, right: 0, top: 0, bottom: 0 },
  labels: { x: [], y: [] },
});

function getChartDims({ width, height, margins }) {
  return {
    chartWidth: width - margins.left - margins.right,
    chartHeight: height - margins.top - margins.bottom,
  };
}

/**
 * @typedef ChartScalesProps
 * @prop {string} chartType
 * @prop {*=} data
 * @prop {number} width
 * @prop {number} height
 * @prop {boolean} publishing
 */
/**
 * @type {React.FC<ChartScalesProps>}
 */
function ChartScales({ chartType, data, width, height, publishing, children }) {
  let scales = DEFAULT_SCALES;
  let xLabels = [];
  let yLabels = [];

  const radial = chartType === 'PieChart';
  const { get, meta } = useReportConfiguration();

  let margins = useMargins({ width, height, compact: publishing, radial });
  let { chartWidth, chartHeight } = getChartDims({ width, height, margins });

  // None of this applies to radial charts - this is only for charts with a
  // horizontal and vertical axis, and radial charts have neither.
  if (!radial) {
    let chartData = [...data];
    // For bar charts, format for reversed, orientation, and percentage.
    if (chartType === 'BarChart') {
      const percentStacking =
        get('percentStack') && get('groupMode') !== 'grouped';
      const reversed = get(`reverse_${get('chartType')}`);
      chartData = formatBarChartData({
        data: JSON.parse(JSON.stringify(data)),
        meta,
        percentStacking,
        reversed,
      });
    }

    // First step:
    //
    // Generate the scales assuming no margin recalculation. We will look at the
    // labels for each of the scales and adjust the margins to show more of the
    // labels if necessary.
    let scales = computeScales({
      chartType,
      getConfiguration: get,
      meta,
      data: chartData,
      width: chartWidth,
      height: chartHeight,
    });

    const isXScale = scale => scale === scales.x;
    const xFormatter = formatAxis({
      configuration: get(),
      axis: 'x',
    });
    const yFormatter = formatAxis({
      configuration: get(),
      axis: 'y',
    });

    const xAxisEnabled = get(['horizontalAxis', 'enabled']);
    const yAxisEnabled = get(['verticalAxis', 'enabled']);

    // Second step:
    //
    // Sort the scales by priority order. If one of the axes has text labels,
    // that label is considered to have a higher priority. If neither scale has
    // text labels, then the y axis is considered to have priority since it is
    // likely to have an axis prefix/suffix/scale.
    const prioritizedScales = [
      yAxisEnabled && scales.y,
      xAxisEnabled && scales.x,
    ]
      .filter(scale => scale)
      .sort((a, b) => {
        if (a.hasTextLabels && !b.hasTextLabels) {
          return -1;
        } else if (!a.hasTextLabels && b.hasTextLabels) {
          return 1;
        } else {
          return 0;
        }
      });

    prioritizedScales.forEach(scale => {
      margins = adjustMarginsForLabel({
        scale,
        isXScale: isXScale(scale),
        getConfiguration: get,
        margins,
        width,
        height,
        formatFn: isXScale(scale) ? xFormatter : yFormatter,
      });
    });

    // Third step:
    //
    // We will need to reset the range of the axes to account for the fact that
    // it has less space now due to the increased margins. We will also need to
    // re-calculate the chart width and height because those will have changed
    // too.
    if (prioritizedScales.length > 0) {
      ({ chartWidth, chartHeight } = getChartDims({ width, height, margins }));
      scales = computeScales({
        chartType,
        getConfiguration: get,
        meta,
        data: chartData,
        width: chartWidth,
        height: chartHeight,
      });
    }

    // Fourth step:
    //
    // After all of this is done, we can calculate the labels to be shown. This
    // will handle label truncation, wrapping, and will handle showing every nth
    // label if needed.
    xLabels = xAxisEnabled
      ? generateLabels({
          scale: scales.x,
          formatFn: xFormatter,
          axisLength: chartWidth,
          angle: get(['horizontalAxis', 'labelSlant']) || 0,
          showTitle: Boolean(get(['horizontalAxis', 'title'])),
          bottomMargin: margins.bottom,
          leftMargin: margins.left,
          rightMargin: margins.right,
        })
      : [];

    yLabels = yAxisEnabled
      ? generateLabels({
          scale: scales.y,
          formatFn: yFormatter,
          axisLength: chartHeight,
          angle: 90 - (get(['verticalAxis', 'labelSlant']) || 0),
          showTitle: Boolean(get(['verticalAxis', 'title'])),
          bottomMargin: margins.left,
          leftMargin: margins.top,
          rightMargin: margins.bottom,
        })
      : [];
  }

  return (
    <ChartScaleContext.Provider
      value={{
        scales,
        margins,
        labels: { x: xLabels, y: yLabels },
      }}
    >
      {children}
    </ChartScaleContext.Provider>
  );
}

export function useChartScales() {
  const context = React.useContext(ChartScaleContext);

  if (!context) {
    throw new Error(
      `useReportConfiguration must be used within a ChartScaleContextProvider`
    );
  }

  return context;
}

export default ChartScales;
